import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Loader2, GraduationCap, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UniversityRegister() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    admin_name: '',
    admin_email: '',
    password: '',
    contact_phone: '',
    website: '',
    address: '',
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be ≥ 8 characters'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/universities/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setDone(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Registration failed');
      }
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 to-background">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-semibold">Registration received</h2>
            <p className="text-sm text-muted-foreground">
              Thanks! Our team will review your university and activate the account.
              You'll receive an email at <b>{form.admin_email}</b> once approved.
            </p>
            <Button onClick={() => navigate('/university/login')} className="w-full">
              Go to University Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background py-8 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          ← Back to Home
        </Link>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div>
                <CardTitle>Register your University</CardTitle>
                <CardDescription>Partner with CollabHive to connect your innovators and labs with industry</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">Institution Name *</Label>
                <Input id="name" required value={form.name} onChange={onChange('name')} placeholder="e.g., Indian Institute of Technology, Bombay" data-testid="uni-name-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="admin_name">Admin Name *</Label>
                  <Input id="admin_name" required value={form.admin_name} onChange={onChange('admin_name')} placeholder="Dr. R. Kumar" data-testid="uni-admin-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input id="phone" value={form.contact_phone} onChange={onChange('contact_phone')} placeholder="+91 98xxxxxxxx" data-testid="uni-phone-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Admin Email * (verified at approval)</Label>
                <Input id="email" type="email" required value={form.admin_email} onChange={onChange('admin_email')} placeholder="admin@university.edu" data-testid="uni-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Set Password * (min 8 chars)</Label>
                <Input id="password" type="password" required value={form.password} onChange={onChange('password')} minLength={8} data-testid="uni-password-input" />
                <p className="text-[10px] text-muted-foreground mt-1">You'll use this to log in after platform admin approves.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input id="website" value={form.website} onChange={onChange('website')} placeholder="https://university.edu" data-testid="uni-website-input" />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={form.address} onChange={onChange('address')} placeholder="City, State" data-testid="uni-address-input" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full gap-2" data-testid="uni-register-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                Submit for Approval
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Already approved? <Link to="/university/login" className="text-primary hover:underline">Log in</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
